import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
} from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { RendererProps } from "../FilePreviewModal";
import CustomScrollbars from "../../CustomScrollbars";
import { useTypedBlobUrl } from "../useTypedBlobUrl";
import SearchBar from "../SearchBar";
import {
  escapeRegex,
  SEARCH_MARK_ACTIVE_CLASS,
  SEARCH_MARK_CLASS,
} from "../searchUtils";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const MIN_SCALE = 0.4;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.2;
const PAGE_GAP = 16;

interface PageEntry {
  width: number;
  height: number;
}

const PdfRenderer: React.FC<RendererProps> = ({ blob, blobUrl, fileName }) => {
  const theme = useTheme();
  const isXsToolbar = useMediaQuery(theme.breakpoints.down("sm"));
  const url = useTypedBlobUrl(blob, blobUrl, "application/pdf");

  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState<number>(1);
  const [fitMode, setFitMode] = useState<"width" | "custom">("width");
  const [containerWidth, setContainerWidth] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pageInputValue, setPageInputValue] = useState("1");
  const [visibleSet, setVisibleSet] = useState<Set<number>>(new Set([1]));
  const [pageDims, setPageDims] = useState<Record<number, PageEntry>>({});
  const [search, setSearch] = useState("");

  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const programmaticScrollRef = useRef(false);

  const documentFile = useMemo(() => (url ? { url } : null), [url]);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const horizontalPad = isXsToolbar ? 8 : 32;
    const update = () => {
      const w = node.clientWidth - horizontalPad;
      setContainerWidth(w > 0 ? w : 0);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [isXsToolbar]);

  const renderWidth = useMemo(() => {
    if (!containerWidth) return undefined;
    if (fitMode === "width") return containerWidth;
    return containerWidth * scale;
  }, [containerWidth, fitMode, scale]);

  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    setNumPages(0);
    setCurrentPage(1);
    setVisibleSet(new Set([1]));
    setPageDims({});
    setError(null);
  }, [url]);

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root || !numPages) return;

    observerRef.current?.disconnect();
    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSet((prev) => {
          const next = new Set(prev);
          let changed = false;
          for (const entry of entries) {
            const target = entry.target as HTMLDivElement;
            const pageNum = Number(target.getAttribute("data-page-number"));
            if (!pageNum) continue;
            if (entry.isIntersecting) {
              if (!next.has(pageNum)) {
                next.add(pageNum);
                next.add(Math.max(1, pageNum - 1));
                next.add(Math.min(numPages, pageNum + 1));
                changed = true;
              }
            }
          }
          return changed ? next : prev;
        });

        if (programmaticScrollRef.current) return;
        const visibleEntries = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visibleEntries.length > 0) {
          const top = visibleEntries[0].target as HTMLDivElement;
          const pageNum = Number(top.getAttribute("data-page-number"));
          if (pageNum) {
            setCurrentPage((prev) => (prev !== pageNum ? pageNum : prev));
          }
        }
      },
      { root, threshold: [0.05, 0.5, 0.95] },
    );

    observerRef.current = observer;
    pageRefs.current.forEach((el) => observer.observe(el));
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [numPages]);

  const registerPageRef = useCallback(
    (pageNumber: number) => (el: HTMLDivElement | null) => {
      const map = pageRefs.current;
      const prev = map.get(pageNumber);
      if (prev && prev !== el) {
        observerRef.current?.unobserve(prev);
      }
      if (el) {
        map.set(pageNumber, el);
        observerRef.current?.observe(el);
      } else {
        map.delete(pageNumber);
      }
    },
    [],
  );

  const scrollToPage = useCallback((pageNumber: number) => {
    const el = pageRefs.current.get(pageNumber);
    if (!el || !scrollerRef.current) return;
    programmaticScrollRef.current = true;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setCurrentPage(pageNumber);
    setVisibleSet((prev) => {
      const next = new Set(prev);
      next.add(pageNumber);
      next.add(Math.max(1, pageNumber - 1));
      next.add(Math.min(numPages, pageNumber + 1));
      return next;
    });
    window.setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 600);
  }, [numPages]);

  const handlePrev = useCallback(() => {
    if (currentPage > 1) scrollToPage(currentPage - 1);
  }, [currentPage, scrollToPage]);

  const handleNext = useCallback(() => {
    if (currentPage < numPages) scrollToPage(currentPage + 1);
  }, [currentPage, numPages, scrollToPage]);

  const handleZoomIn = useCallback(() => {
    setFitMode("custom");
    setScale((s) => Math.min(MAX_SCALE, +(s + ZOOM_STEP).toFixed(2)));
  }, []);
  const handleZoomOut = useCallback(() => {
    setFitMode("custom");
    setScale((s) => Math.max(MIN_SCALE, +(s - ZOOM_STEP).toFixed(2)));
  }, []);
  const handleFitWidth = useCallback(() => {
    setFitMode("width");
    setScale(1);
  }, []);

  const handlePageInputCommit = useCallback(
    (value: string) => {
      const n = parseInt(value, 10);
      if (Number.isFinite(n) && n >= 1 && n <= numPages) {
        scrollToPage(n);
      } else {
        setPageInputValue(String(currentPage));
      }
    },
    [currentPage, numPages, scrollToPage],
  );

  const handleDocumentLoad = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setVisibleSet(new Set([1, 2].filter((x) => x <= n)));
    setError(null);
  }, []);

  const handleDocumentError = useCallback((err: Error) => {
    setError(err.message || "Failed to load PDF");
  }, []);

  const handlePageRender = useCallback(
    (pageNumber: number) =>
      ({ width, height }: { width: number; height: number }) => {
        setPageDims((prev) => {
          const existing = prev[pageNumber];
          if (
            existing &&
            existing.width === width &&
            existing.height === height
          ) {
            return prev;
          }
          return { ...prev, [pageNumber]: { width, height } };
        });
      },
    [],
  );

  const documentOptions = useMemo(
    () => ({
      cMapUrl: `//unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `//unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    }),
    [],
  );

  const customTextRenderer = useCallback(
    (textItem: { str: string }) => {
      const raw = textItem.str ?? "";
      if (!search) return raw;
      const escaped = raw
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      const re = new RegExp(`(${escapeRegex(search)})`, "gi");
      return escaped.replace(
        re,
        `<mark class="${SEARCH_MARK_CLASS}">$1</mark>`,
      );
    },
    [search],
  );

  const [scrollerEl, setScrollerEl] = useState<HTMLElement | null>(null);
  const setSearchScrollerRef = useCallback(
    (node: HTMLElement | null) => {
      setScrollerEl(node);
      scrollerRef.current = node as unknown as HTMLElement | null;
    },
    [],
  );

  const [searchTotal, setSearchTotal] = useState(0);
  const [searchCurrent, setSearchCurrent] = useState(0);

  useEffect(() => {
    setSearchCurrent(0);
  }, [search]);

  useEffect(() => {
    if (!scrollerEl) {
      setSearchTotal(0);
      return;
    }
    if (!search) {
      setSearchTotal(0);
      setSearchCurrent(0);
      return;
    }
    const recompute = () => {
      const marks = scrollerEl.querySelectorAll(
        `mark.${SEARCH_MARK_CLASS}`,
      );
      setSearchTotal(marks.length);
      setSearchCurrent((prev) => {
        if (marks.length === 0) return 0;
        if (prev === 0) return 1;
        if (prev > marks.length) return marks.length;
        return prev;
      });
    };
    recompute();
    const observer = new MutationObserver(recompute);
    observer.observe(scrollerEl, {
      subtree: true,
      childList: true,
      characterData: true,
    });
    return () => observer.disconnect();
  }, [scrollerEl, search]);

  useEffect(() => {
    if (!scrollerEl) return;
    const marks = scrollerEl.querySelectorAll(
      `mark.${SEARCH_MARK_CLASS}`,
    );
    if (searchCurrent <= 0 || marks.length === 0) {
      marks.forEach((m) => m.classList.remove(SEARCH_MARK_ACTIVE_CLASS));
      return;
    }
    marks.forEach((m, i) => {
      if (i === searchCurrent - 1) {
        m.classList.add(SEARCH_MARK_ACTIVE_CLASS);
      } else {
        m.classList.remove(SEARCH_MARK_ACTIVE_CLASS);
      }
    });
    const active = marks[searchCurrent - 1] as HTMLElement | undefined;
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [scrollerEl, searchCurrent, searchTotal]);

  const searchGotoNext = useCallback(() => {
    setSearchCurrent((prev) => {
      if (searchTotal === 0) return 0;
      return prev >= searchTotal ? 1 : prev + 1;
    });
  }, [searchTotal]);

  const searchGotoPrev = useCallback(() => {
    setSearchCurrent((prev) => {
      if (searchTotal === 0) return 0;
      return prev <= 1 ? searchTotal : prev - 1;
    });
  }, [searchTotal]);

  if (!url) return null;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        sx={{
          px: { xs: 1, sm: 2 },
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          flexWrap="wrap"
          sx={{
            rowGap: 0.5,
            columnGap: 0.5,
            minWidth: 0,
            flex: { sm: 1 },
          }}
        >
          <Tooltip title="Previous page">
            <span>
              <IconButton
                size="small"
                onClick={handlePrev}
                disabled={currentPage <= 1 || !numPages}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <TextField
              size="small"
              value={pageInputValue}
              onChange={(e) => setPageInputValue(e.target.value)}
              onBlur={() => handlePageInputCommit(pageInputValue)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handlePageInputCommit((e.target as HTMLInputElement).value);
                  (e.target as HTMLInputElement).blur();
                }
              }}
              inputProps={{
                style: {
                  width: 36,
                  textAlign: "center",
                  padding: "4px 6px",
                  fontSize: 13,
                },
                "aria-label": "Page number",
              }}
              disabled={!numPages}
            />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              / {numPages || "—"}
            </Typography>
          </Stack>
          <Tooltip title="Next page">
            <span>
              <IconButton
                size="small"
                onClick={handleNext}
                disabled={!numPages || currentPage >= numPages}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Box
            sx={{
              flex: 1,
              minWidth: 8,
              display: { xs: "none", sm: "block" },
            }}
          />
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                onClick={handleZoomOut}
                disabled={scale <= MIN_SCALE}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Box
            component="span"
            sx={{
              fontSize: 12,
              minWidth: 44,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            {fitMode === "width" ? "Fit" : `${Math.round(scale * 100)}%`}
          </Box>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                onClick={handleZoomIn}
                disabled={scale >= MAX_SCALE}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Fit to width">
            <IconButton size="small" onClick={handleFitWidth}>
              <FitScreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!isXsToolbar && (
            <SearchBar
              value={search}
              onChange={setSearch}
              matchCount={searchTotal}
              currentMatch={searchCurrent}
              onPrev={searchGotoPrev}
              onNext={searchGotoNext}
              minWidth={140}
              sx={{ ml: { sm: 0.5 }, maxWidth: { sm: 360 } }}
            />
          )}
        </Stack>
        {isXsToolbar && (
          <SearchBar
            value={search}
            onChange={setSearch}
            matchCount={searchTotal}
            currentMatch={searchCurrent}
            onPrev={searchGotoPrev}
            onNext={searchGotoNext}
            minWidth={0}
            sx={{ width: "100%" }}
          />
        )}
      </Stack>

      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          backgroundColor: (t) => t.palette.grey[200],
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomScrollbars
          ref={(node) =>
            setSearchScrollerRef(node as unknown as HTMLElement | null)
          }
        >
          <Box sx={{ px: { xs: 0.5, sm: 2 }, py: { xs: 1, sm: 2 } }}>
            {error ? (
              <Alert severity="error" sx={{ mx: "auto", maxWidth: 480 }}>
                {error}
              </Alert>
            ) : (
              <Document
                file={documentFile}
                onLoadSuccess={handleDocumentLoad}
                onLoadError={handleDocumentError}
                options={documentOptions}
                loading={
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={1.5}
                    sx={{ py: 6 }}
                  >
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary">
                      Loading PDF…
                    </Typography>
                  </Stack>
                }
                error={
                  <Alert severity="error" sx={{ mx: "auto", maxWidth: 480 }}>
                    Failed to load PDF
                  </Alert>
                }
                noData={
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 6 }}
                  >
                    No PDF data
                  </Typography>
                }
              >
                <Stack
                  spacing={`${PAGE_GAP}px`}
                  sx={{ alignItems: "center" }}
                >
                  {Array.from({ length: numPages }).map((_, idx) => {
                    const pageNumber = idx + 1;
                    const dims = pageDims[pageNumber];
                    const placeholderHeight = dims
                      ? dims.height
                      : renderWidth
                        ? renderWidth * 1.4
                        : 800;
                    const isVisible = search ? true : visibleSet.has(pageNumber);
                    return (
                      <Box
                        key={pageNumber}
                        ref={registerPageRef(pageNumber)}
                        data-page-number={pageNumber}
                        sx={{
                          backgroundColor: "background.paper",
                          boxShadow: 2,
                          borderRadius: 1,
                          overflow: "hidden",
                          position: "relative",
                          minWidth: dims?.width ?? renderWidth,
                          minHeight: placeholderHeight,
                        }}
                      >
                        {isVisible ? (
                          <Page
                            pageNumber={pageNumber}
                            width={renderWidth}
                            renderAnnotationLayer
                            renderTextLayer
                            customTextRenderer={customTextRenderer}
                            onRenderSuccess={handlePageRender(pageNumber)}
                            loading={
                              <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  width: dims?.width ?? renderWidth,
                                  height: placeholderHeight,
                                }}
                              >
                                <CircularProgress size={20} />
                              </Stack>
                            }
                          />
                        ) : (
                          <Stack
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              width: dims?.width ?? renderWidth,
                              height: placeholderHeight,
                              color: "text.secondary",
                            }}
                          >
                            <Typography variant="caption">
                              Page {pageNumber}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Document>
            )}
          </Box>
        </CustomScrollbars>
      </Box>
    </Box>
  );
};

export default PdfRenderer;
